/**
 * Clock values on a time card travel as `HH:MM` in the form and `HHMM` to the
 * API, which stores them as text and compares them as numbers.
 */

/** Minutes since midnight for `HH:MM`, or null when it is not a valid 24-hour time. */
export function parseClock(value: string | null | undefined): number | null {
  if (!value) return null
  const match = /^(\d{2}):(\d{2})$/.exec(value)
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null
  return hours * 60 + minutes
}

/** Hours between two clock values as a string with two decimals, or "" when either is invalid or end is not after start. */
export function hoursBetween(start: string, end: string): string {
  const s = parseClock(start)
  const e = parseClock(end)
  if (s === null || e === null || e < s) return ''
  return ((e - s) / 60).toFixed(2)
}

/** `HH:MM` -> `HHMM`, the shape the API stores. */
export function toApiClock(value: string): string {
  return value.replace(':', '')
}

/** `HHMM` (as stored) -> `HH:MM` for display; anything else is returned as is. */
export function fromApiClock(value: string | null | undefined): string {
  if (!value) return ''
  return /^\d{4}$/.test(value) ? `${value.slice(0, 2)}:${value.slice(2)}` : value
}

/**
 * The window in which time may be entered: Monday 00:00 through Friday 16:59,
 * Philadelphia time. Saturday, Sunday and Friday from 17:00 are closed. The
 * server does not enforce this yet (see the modernization plan, section 2.6.4).
 */
export function isEntryWindowClosed(now: Date = new Date()): boolean {
  const local = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }))
  const day = local.getDay() // 0 = Sunday, 5 = Friday, 6 = Saturday
  if (day === 6 || day === 0) return true
  if (day === 5) return local.getHours() >= 17
  return false
}
